import { Capacitor, registerPlugin } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { createMirrorAuthSessionScope, mirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import { platformOpen } from "@/lib/platformOpen";
import { isNativePeopleProvider, isPeopleTaskWrite, type PeopleTaskRequest, type PeopleTaskProvider, type PeopleTaskItem } from "../../supabase/functions/_shared/peopleTasksDomain";
export { isContactProvider, isNativePeopleProvider, isPeopleTaskWrite, hasPeopleTaskWriteScope } from "../../supabase/functions/_shared/peopleTasksDomain";
export type { PeopleTaskRequest, PeopleTaskProvider, PeopleTaskItem, PeopleTaskAction } from "../../supabase/functions/_shared/peopleTasksDomain";

interface NativePermission { status: "authorized" | "limited" | "denied" | "notDetermined"; device_id: string }
export interface PeopleTaskResult {
  ok?: boolean; native?: boolean; device_id?: string; user_id?: string; request_hash?: string; replayed?: boolean;
  item?: PeopleTaskItem; items?: PeopleTaskItem[]; lists?: PeopleTaskItem[]; next_cursor?: string | null;
  local_sync?: { status: "ok" | "conflict" | "failed" | "skipped"; change: "inserted" | "updated" | "deleted" | "unchanged" | null; task_id?: string; external_id?: string; error?: string };
  local_task?: unknown;
}
interface NativePeopleTasks {
  permission(input: { provider: PeopleTaskProvider }): Promise<NativePermission>;
  requestPermission(input: { provider: PeopleTaskProvider }): Promise<NativePermission>;
  execute(input: PeopleTaskRequest & { user_id?: string; request_hash?: string }): Promise<PeopleTaskResult>;
}
const native = registerPlugin<NativePeopleTasks>("PeopleTasks");
export const nativePeopleTasksAvailable = () => Capacitor.getPlatform() === "ios" && Capacitor.isPluginAvailable("PeopleTasks");
export function visiblePeopleTaskProviders(validationAccount = false): PeopleTaskProvider[] {
  return [
    ...((FEATURE_FLAGS.GOOGLE_CONTACTS_ENABLED || validationAccount) ? ["google_contacts" as const] : []),
    ...((FEATURE_FLAGS.MICROSOFT_CONTACTS_ENABLED || validationAccount) ? ["microsoft_contacts" as const] : []),
    ...(FEATURE_FLAGS.APPLE_CONTACTS_ENABLED && nativePeopleTasksAvailable() ? ["apple_contacts" as const] : []),
    ...((FEATURE_FLAGS.PEOPLE_TASKS_EXECUTOR_ENABLED || validationAccount) ? ["google_tasks" as const] : []),
    ...((FEATURE_FLAGS.MICROSOFT_TODO_ENABLED || validationAccount) ? ["microsoft_todo" as const] : []),
    ...(FEATURE_FLAGS.APPLE_REMINDERS_ENABLED && nativePeopleTasksAvailable() ? ["apple_reminders" as const] : []),
  ];
}
export async function connectPeopleTasks(provider: PeopleTaskProvider, writeAccess = false) {
  if (!isNativePeopleProvider(provider)) {
    const { data, error } = await supabase.functions.invoke("people-tasks-oauth-init", { body: { provider, write_access: writeAccess } });
    if (error || !data?.authorize_url) throw error || new Error("connection_failed");
    await platformOpen(data.authorize_url);
    return;
  }
  if (!nativePeopleTasksAvailable()) throw new Error("native_unavailable");
  const permission = await native.requestPermission({ provider });
  if (!["authorized", "limited"].includes(permission.status)) throw new Error("permission_denied");
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("invalid_session");
  const { error } = await supabase.from("user_connections").upsert({ user_id: user.id, provider, status: "active", scope: ["device"], provider_metadata: { transport: "native", permission: permission.status }, last_sync_error: null }, { onConflict: "user_id,provider" });
  if (error) throw error;
}
export async function peopleTaskDeviceId(provider: PeopleTaskProvider): Promise<string> {
  if (!nativePeopleTasksAvailable()) throw new Error("native_unavailable");
  const permission = await native.permission({ provider });
  if (!["authorized", "limited"].includes(permission.status)) throw new Error("permission_denied");
  return permission.device_id;
}
export async function invokePeopleTask(input: PeopleTaskRequest, expectedOwner?: string, prepared?: PeopleTaskResult, isCurrent: () => boolean = () => true): Promise<PeopleTaskResult> {
  if (input.provider === "apple_reminders" && !isPeopleTaskWrite(input.action)) return invokeReminderRead(input,expectedOwner,isCurrent);
  if (input.provider === "apple_reminders" && isPeopleTaskWrite(input.action)) return invokeReminderWrite(input, expectedOwner, prepared, isCurrent);
  const body = { ...input };
  const isNative = isNativePeopleProvider(body.provider);
  if (isNative) {
    if (!nativePeopleTasksAvailable()) throw new Error("native_unavailable");
    const permission = await native.permission({ provider: body.provider });
    if (!["authorized", "limited"].includes(permission.status)) throw new Error("permission_denied");
    if (body.device_id && body.device_id !== permission.device_id) throw new Error("device_changed");
    body.device_id = permission.device_id;
  }
  let headers: Record<string, string> | undefined;
  if (expectedOwner) {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.id !== expectedOwner) throw new Error("invalid_session");
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || session?.user.id !== expectedOwner || !session.access_token) throw new Error("invalid_session");
    // Bind every phase to this owner even if the client switches accounts in flight.
    headers = { Authorization: "Bearer " + session.access_token };
  }
  if (prepared && (!isNative || !isPeopleTaskWrite(body.action) || !expectedOwner || prepared.user_id !== expectedOwner || !prepared.request_hash || !prepared.native)) throw new Error("invalid_preparation");
  const { data, error } = prepared ? { data: prepared, error: null } : await supabase.functions.invoke("people-tasks-executor", { body, ...(headers ? { headers } : {}) });
  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error || "executor_failed");
  if (!data.native) return data;
  if (expectedOwner && isPeopleTaskWrite(body.action) && data.user_id !== expectedOwner) throw new Error("invalid_session");
  let result: PeopleTaskResult;
  try { result = await native.execute({ ...body, user_id: data.user_id, request_hash: data.request_hash }); }
  catch (cause) { throw isPeopleTaskWrite(body.action) ? new Error("outcome_unknown") : cause; }
  if (!isPeopleTaskWrite(body.action)) return result;
  const finished = await supabase.functions.invoke("people-tasks-executor", { body: { ...body, phase: "finish_native", native_result: result.item }, ...(headers ? { headers } : {}) });
  if (finished.error || !finished.data?.ok) throw new Error("outcome_unknown");
  return finished.data;
}

/** Native mutation, remote receipt and local observation are separate phases.
 * GET failure keeps the confirmed mutation successful; it never retries the mutation. */
async function invokeReminderWrite(input: PeopleTaskRequest, owner: string | undefined, prepared: PeopleTaskResult | undefined, isCurrent: () => boolean): Promise<PeopleTaskResult> {
  if (!isCurrent()) throw new Error("invalid_session");
  const original = await supabase.auth.getSession();
  const identity = mirrorAuthIdentity(original.data.session);
  if (!isCurrent() || original.error || !identity || owner && identity.ownerId !== owner) throw new Error("invalid_session");
  owner = identity.ownerId;
  const scope = createMirrorAuthSessionScope();
  scope.observeSession(original.data.session);
  const lease = scope.capture()!;
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => scope.observeSession(session));
  const check = async () => {
    if (!isCurrent() || !scope.isCurrent(lease)) throw new Error("invalid_session");
    const session = await supabase.auth.getSession();
    if (!isCurrent() || session.error || !scope.matchesSession(session.data.session, lease)) throw new Error("invalid_session");
  };
  const headers = { Authorization: "Bearer " + identity.accessToken };
  try {
    await check();
    if (!nativePeopleTasksAvailable()) throw new Error("native_unavailable");
    const permission = await native.permission({ provider: input.provider });
    await check();
    if (!["authorized","limited"].includes(permission.status)) throw new Error("permission_denied");
    if (input.device_id && permission.device_id !== input.device_id) throw new Error("device_changed");
    const body = { ...input, device_id: permission.device_id };
    if (prepared && (!prepared.native || prepared.user_id !== owner || !prepared.request_hash)) throw new Error("invalid_preparation");
    const first = prepared ? {data:prepared,error:null} : await supabase.functions.invoke("people-tasks-executor",{body,headers});
    await check();
    if (first.error || !first.data?.ok) throw first.error || new Error(first.data?.error || "executor_failed");
    let completed: PeopleTaskResult = first.data;
    if (completed.native) {
      if (completed.user_id !== owner || !completed.request_hash) throw new Error("invalid_preparation");
      await check();
      let write: PeopleTaskResult;
      try { write = await native.execute({...body,user_id:owner,request_hash:completed.request_hash}); }
      catch { throw new Error("outcome_unknown"); }
      await check();
      const finish = await supabase.functions.invoke("people-tasks-executor",{body:{...body,phase:"finish_native",native_result:write.item},headers});
      await check();
      if (finish.error || !finish.data?.ok) throw new Error("outcome_unknown");
      completed = finish.data;
    }
    if (completed.local_sync?.status === "ok") return completed;
    const id = completed.item?.id;
    if (!id || input.action !== "create" && id !== input.item_id) return completed;
    try {
      await check();
      const begun = await supabase.functions.invoke("people-tasks-executor",{body:{...body,phase:"observe_native"},headers});
      await check();
      const grant = begun.data?.native_observation_grant;
      if (begun.error || !begun.data?.ok || !grant) return completed;
      if (grant.local_sync) return {...completed,local_sync:grant.local_sync};
      if (typeof grant.nonce !== "string" || typeof grant.expires_at !== "string" || Date.parse(grant.expires_at)<=Date.now()) return completed;
      const currentPermission = await native.permission({provider:input.provider});
      await check();
      if (!["authorized","limited"].includes(currentPermission.status) || currentPermission.device_id !== body.device_id) return completed;
      // Existing read endpoint admits/quota-checks the selected list/item before local GET.
      const get = {provider:body.provider,action:"get" as const,list_id:body.list_id,item_id:id,device_id:body.device_id};
      const admitted = await supabase.functions.invoke("people-tasks-executor",{body:get,headers});
      await check();
      if (admitted.error || !admitted.data?.ok || !admitted.data.native) return completed;
      let observation: Record<string,unknown>;
      try {
        const fresh = await native.execute(get);
        await check();
        observation = {item:fresh.item};
      } catch (cause) {
        await check();
        if (input.action !== "delete" || !(cause instanceof Error) || cause.message !== "item_not_found") return completed;
        observation = {id,list_id:body.list_id,deleted:true};
      }
      const afterPermission = await native.permission({provider:input.provider});
      await check();
      if (afterPermission.device_id !== body.device_id || !["authorized","limited"].includes(afterPermission.status)) return completed;
      const sync = await supabase.functions.invoke("people-tasks-executor",{body:{...body,phase:"reconcile_native",native_observation:{...observation,observation_nonce:grant.nonce}},headers});
      await check();
      if (!sync.error && sync.data?.ok) completed = sync.data;
    } catch (cause) {
      await check(); // Owner/SID/ABA loss is never converted into a publishable old success.
      if (cause instanceof Error && cause.message === "invalid_session") throw cause;
    }
    return completed;
  } finally { scope.invalidate(); subscription.unsubscribe(); }
}

/** Device and actor are rechecked around the quota-admitted native read.
 * The returned device locator comes from permission, not arbitrary item metadata. */
async function invokeReminderRead(input: PeopleTaskRequest, owner: string | undefined, isCurrent:()=>boolean): Promise<PeopleTaskResult> {
  if(!isCurrent())throw new Error("invalid_session");
  const original=await supabase.auth.getSession(),identity=mirrorAuthIdentity(original.data.session);
  if(!isCurrent()||original.error||!identity||owner&&identity.ownerId!==owner)throw new Error("invalid_session");
  const scope=createMirrorAuthSessionScope();scope.observeSession(original.data.session);const lease=scope.capture()!;
  const {data:{subscription}}=supabase.auth.onAuthStateChange((_event,session)=>scope.observeSession(session));
  const check=async()=>{
    if(!isCurrent()||!scope.isCurrent(lease))throw new Error("invalid_session");
    const session=await supabase.auth.getSession();
    if(!isCurrent()||session.error||!scope.matchesSession(session.data.session,lease))throw new Error("invalid_session");
  };
  try {
    await check();
    if(!nativePeopleTasksAvailable())throw new Error("native_unavailable");
    const permission=await native.permission({provider:input.provider});await check();
    if(!["authorized","limited"].includes(permission.status))throw new Error("permission_denied");
    if(input.device_id&&input.device_id!==permission.device_id)throw new Error("device_changed");
    const body={...input,device_id:permission.device_id};
    const response=await supabase.functions.invoke("people-tasks-executor",{body,headers:{Authorization:"Bearer "+identity.accessToken}});
    await check();
    if(response.error||!response.data?.ok||!response.data.native)throw response.error||new Error(response.data?.error||"executor_failed");
    const before=await native.permission({provider:input.provider});await check();
    if(before.device_id!==body.device_id)throw new Error("device_changed");
    if(!["authorized","limited"].includes(before.status))throw new Error("permission_denied");
    const result=await native.execute(body);await check();
    const after=await native.permission({provider:input.provider});await check();
    if(after.device_id!==body.device_id)throw new Error("device_changed");
    if(!["authorized","limited"].includes(after.status))throw new Error("permission_denied");
    return {...result,device_id:body.device_id};
  } finally {scope.invalidate();subscription.unsubscribe();}
}
